import type { OnChangeFn } from "@dresscode-frontend/utility-types";
import { useCallback, useState } from "react";

import { waitForPopoverAnimation } from "@/utils/ui";

type SelectedOption = {
  id: string;
  label: string;
  [key: string]: unknown;
};

/**
 * カスタムフック: useMultiSelectAvailableFilterChipModel
 * 複数選択可能なフィルターチップのモデルを管理します。
 */
export default function useMultiSelectAvailableFilterChipModel<
  TSelectedOption extends SelectedOption,
>(options?: {
  isFreeTextEachChangeInitial?: boolean;
}): {
  open: boolean;
  selectedIds: string[];
  selectedOptions: TSelectedOption[];
  idToLabel: Record<string, string>;
  freeText: string;
  onFreeTextChange: (text: string) => void;
  getOnSelectedIdsChange: (deps: {
    options: TSelectedOption[];
  }) => (ids: string[], selectedOptions?: TSelectedOption[]) => void;
  getOnOpenChange: (deps: {
    onFilterChange: OnChangeFn<string[] | undefined>;
  }) => (newOpen: boolean) => Promise<void>;
} {
  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // NOTE: idsのみ保持するよりは構造化データを扱ったほうが使いやすいケースもあるため別で管理
  const [selectedOptions, setSelectedOptions] = useState<TSelectedOption[]>([]);
  const [idToLabel, setIdToLabel] = useState<Record<string, string>>({});
  const [freeText, setFreeText] = useState("");

  const getOnSelectedIdsChange = useCallback(
    (deps: { options: TSelectedOption[] }) =>
      (ids: string[], selectedOptions?: TSelectedOption[]) => {
        setSelectedIds(ids);
        if (selectedOptions) setSelectedOptions(selectedOptions);
        if (options?.isFreeTextEachChangeInitial) setFreeText("");

        const newIdToLabel = ids
          .map((id) => deps.options?.find((op) => op.id === id))
          .filter((opt) => !!opt)
          .reduce(
            (acc, opt) => {
              acc[opt.id] = opt.label;
              return acc;
            },
            {} as Record<string, string>,
          );
        setIdToLabel((prev) => {
          const updated = { ...prev };
          for (const key in newIdToLabel) {
            updated[key] = newIdToLabel[key];
          }
          return updated;
        });
      },
    [options?.isFreeTextEachChangeInitial],
  );

  const getOnOpenChange = useCallback(
    (deps: { onFilterChange: OnChangeFn<string[] | undefined> }) =>
      async (newOpen: boolean) => {
        setOpen(newOpen);
        await waitForPopoverAnimation();
        if (!newOpen) {
          deps.onFilterChange(
            (selectedIds.length ?? 0) > 0 ? selectedIds : undefined,
          );
        }
      },
    [selectedIds],
  );

  return {
    open,
    selectedIds,
    selectedOptions,
    idToLabel,
    freeText,
    onFreeTextChange: setFreeText,
    getOnSelectedIdsChange,
    getOnOpenChange,
  };
}
